// Repair for a leg the operator REFUSED because its stake id has rotated.
//
// ⛔ THE FAILURE THIS ANSWERS. A stake id names a PRICE, not an outcome: when the
// book takes a market down and re-opens it, the same outcome comes back under a NEW
// id. A card that holds the old one posts it, the operator answers
// `ERROR_STAKE_DOS_NOT_EXIST` (code 1304), and the add dies with nothing landed —
// the live `add rejected posted=N landed=0`. The identity that SURVIVES the rotation
// is `(eventId, marketId, code, argument)`, and the catalog can resolve it back onto
// the stake id the board is using now.
//
// The decision is a pure function of (legs, resolve) so it can be tested without a
// DOM, a renderer or a live operator — the same reason `add-resolution.ts` is pure.

import type { Suggestion } from "./types";
import type { StakePair } from "./add-resolution";

/** An outcome named the way it survives a stake-id rotation. */
export interface OutcomeIdentity {
  eventId: number;
  /** Digitain `StakeTypeId` — 1 = Исход (1X2), 3 = Тотал, 37 = double chance, … */
  marketId: number;
  /** Digitain `SC` / the coupon's `StakeCode`. */
  code: number;
  /** Digitain `A` / the coupon's `Argument`; null for a market with no line. */
  argument: number | null;
}

/** One leg this add posted, with what it takes to re-resolve it. `identity` is null
 *  when the card cannot name its outcome (an older payload, or a line the board sent
 *  without an `SC`) — then the leg is simply not repairable, never guessed at. */
export interface PostedLeg {
  stakeId: number;
  identity: OutcomeIdentity | null;
}

/** The legs of a suggestion, each with the identity a repair keys on: the combo's
 *  own legs, or the single card's own fields. */
export function legIdentities(s: Suggestion): PostedLeg[] {
  const legs = s.legs && s.legs.length ? s.legs : [null];
  return legs.map((l) => {
    const stakeId = Number(l ? l.stake_id : s.stake_id);
    const eventId = Number(l ? l.event_id : s.event_id);
    if (!Number.isFinite(stakeId) || stakeId <= 0) {
      return { stakeId: Number.isFinite(stakeId) ? stakeId : NaN, identity: null };
    }
    const marketId = Number(l ? l.market_id : s.market_id);
    const code = Number(l ? l.code : s.code);
    const rawArg = l ? l.line : s.line;
    const argument = rawArg == null ? null : Number(rawArg);
    const named = Number.isFinite(eventId) && eventId > 0
      && Number.isFinite(marketId) && Number.isFinite(code);
    return {
      stakeId,
      identity: named ? { eventId, marketId, code, argument } : null,
    };
  });
}

/**
 * The (stakeId, eventId) pairs to re-post for legs the operator refused: each
 * refused leg re-resolved onto the id the board is offering NOW.
 *
 * ⛔ `rejected` ONLY, and only on an operator DECLINE. `toggleStake` is symmetric,
 * so re-posting a leg that IS in the coupon REMOVES it — and the licence to re-post
 * here is that the operator answered "none of these landed". A timeout is not that
 * answer, and a partial add has legs the user is already holding.
 *
 * A leg is skipped, never guessed at, when:
 *   • the card cannot name its outcome (`identity` null),
 *   • the board is not offering that outcome right now (resolve → null) — a market
 *     like «Исход с 1-50 мин.» expires outright and never comes back, and
 *     substituting a different outcome is the one thing that must never happen,
 *   • the board already names the same id we posted — nothing rotated, and there is
 *     nothing to fix.
 * Two legs resolving onto the SAME id collapse to one: posting one stake twice
 * toggles it straight back out.
 */
export function planRepost(
  legs: PostedLeg[],
  resolve: (o: OutcomeIdentity) => { stakeId: string; odds: number } | null,
): StakePair[] {
  const out: StakePair[] = [];
  const seen = new Set<number>();
  for (const leg of legs) {
    if (!leg.identity) continue;
    let live: { stakeId: string; odds: number } | null = null;
    try {
      live = resolve(leg.identity);
    } catch {
      live = null;          // a resolve that throws is "not on offer", not a crash
    }
    if (!live) continue;
    const stakeId = Number(live.stakeId);
    if (!Number.isFinite(stakeId) || stakeId <= 0) continue;
    if (stakeId === leg.stakeId) continue;      // same id: nothing to repair
    if (seen.has(stakeId)) continue;
    seen.add(stakeId);
    out.push({ stakeId, eventId: leg.identity.eventId });
  }
  return out;
}
